// Adapter between core-api opportunity wire shape and the hermes-side shape.
// Mirrors the dashboard adapter — kept as a local copy since hermes and
// dashboard are independent repos.

export const CORE_API_URL = process.env.CORE_API_URL || 'http://localhost:8090'

// Webpack 4 in hermes does not parse `??`.
function nullish(value, fallback) {
  return value === null || value === undefined ? fallback : value
}

const TYPE_LABELS = {
  'full-time': 'Full-Time',
  'part-time': 'Part-Time',
  hourly: 'Hourly',
  contract: 'Contract',
  internship: 'Internship',
}

export const STATUS_LABELS = {
  in_review: 'In Review',
  active: 'Approved',
  closed: 'Closed',
  rejected: 'Rejected',
  incomplete: 'Incomplete',
  quarantined: 'Quarantined',
}

export function statusLabel(status) {
  return STATUS_LABELS[status] || status || ''
}

function formatLocation(loc) {
  if (!loc) return ''
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ')
  const tail = loc.remote ? ' (Remote)' : loc.raw ? ` (${loc.raw})` : ''
  return cityState + tail
}

export function formatSalaryRange(salary) {
  if (!salary) return null
  const { min, max, period } = salary
  if (min == null && max == null) return null
  const fmt = (n) => (n < 1000 ? `$${n}` : `$${Math.round(n / 1000)}k`)
  const range =
    min != null && max != null
      ? `${fmt(min)}-${fmt(max)}`
      : min != null
      ? `${fmt(min)}+`
      : `Up to ${fmt(max)}`
  const suffix = period === 'hour' ? ' / hr' : period === 'month' ? ' / mo' : ' / yr'
  return range + suffix
}

export function coreApiOpportunityToHermes(raw) {
  if (!raw || typeof raw !== 'object') return null
  const submitter = (raw.source && raw.source.submitter) || {}
  return {
    _id: raw._id,
    // core-api stores the community name in `community` (String) and the
    // Mongo ObjectId in `communityRel` (ref: Community). We need the ObjectId
    // to hit `/communities/:id/community-request` for brand styling.
    communityId: raw.communityRel || raw.communityId || null,
    communityName: raw.community || null,
    title: raw.title || '',
    company: raw.company || '',
    location: formatLocation(raw.location),
    locationStruct: raw.location || null,
    jobType: TYPE_LABELS[raw.type] || raw.type || '',
    type: raw.type || null,
    status: raw.status || 'in_review',
    submittedDate: raw.submissionDate || raw.createdAt || null,
    expirationDate: raw.expirationDate || null,
    deadline: raw.deadline || null,
    description: raw.description || '',
    applicationUrl: raw.applicationURL || '',
    industry: raw.industry || [],
    skills: raw.skills || [],
    salary: raw.salary
      ? {
          start: nullish(raw.salary.min, null),
          end: nullish(raw.salary.max, null),
          currency: raw.salary.currency || 'USD',
          period: raw.salary.period || 'year',
          disclosed: nullish(raw.salary.disclosed, null),
          display: formatSalaryRange(raw.salary),
        }
      : null,
    requiredExperienceYears: nullish(raw.requiredExperienceYears, null),
    requiredEducation: raw.requiredEducation || null,
    source: {
      channel: (raw.source && raw.source.channel) || 'program-page',
      firstName: submitter.firstName || null,
      lastName: submitter.lastName || null,
      email: submitter.email || null,
    },
    engagement: {
      emailed: (raw.engagement && raw.engagement.emailed) || 0,
      clicked: (raw.engagement && raw.engagement.clicked) || 0,
      applied: (raw.engagement && raw.engagement.applied) || 0,
      secured: (raw.engagement && raw.engagement.secured) || 0,
    },
    clickCount: (raw.engagement && raw.engagement.clicked) || 0,
    statusHistory: raw.statusHistory || [],
    activityLog: raw.activityLog || [],
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  }
}

export function coreApiPageToHermesList(page) {
  const list = Array.isArray(page) ? page : (page && page.data) || []
  return list.map(coreApiOpportunityToHermes).filter(Boolean)
}



// WEBPACK FOOTER //
// ./src/services/opportunityAdapter.js